import type { TranslationKey } from '../i18n/locale';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from '../api/client';

export const PlatformRole = {
  Admin: 'admin',
  SuperAdmin: 'superadmin',
} as const;
export type PlatformRole = (typeof PlatformRole)[keyof typeof PlatformRole];

export const PLATFORM_ROLE_LABEL: Record<PlatformRole, TranslationKey> = {
  [PlatformRole.Admin]: 'platformRole.admin',
  [PlatformRole.SuperAdmin]: 'platformRole.superadmin',
};

export interface PlatformAdmin {
  id: string;
  email: string;
  name: string;
  role: PlatformRole;
  mustChangePassword: boolean;
}

interface AdminSession {
  admin: PlatformAdmin | null;
  loading: boolean;
  isSuper: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AdminSession | null>(null);

/**
 * Session state for the platform area.
 *
 * Deliberately a separate provider from the tenant one in lib/session, not a
 * mode of it. They authenticate against different tables with different
 * cookies, and the admin area must never be able to read or refresh a
 * customer session by accident.
 */
export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const d = await api.get<{ admin: PlatformAdmin }>('/platform/me');
      setAdmin(d.admin);
    } catch {
      // 401 is the ordinary signed-out case, not an error worth surfacing.
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const d = await api.post<{ admin: PlatformAdmin }>('/platform/login', { email, password });
    setAdmin(d.admin);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/platform/logout');
    } catch (e) {
      // Already gone server-side is still logged out here.
      if (!(e instanceof ApiError)) throw e;
    }
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({
      admin,
      loading,
      isSuper: admin?.role === PlatformRole.SuperAdmin,
      login,
      logout,
      refresh,
    }),
    [admin, loading, login, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAdminSession(): AdminSession {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAdminSession used outside AdminSessionProvider');
  return ctx;
}
